import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BeheerdersportaalSidebar from '../components/BeheerdersportaalSidebar';
import api from '../components/api';
import { useToast } from '../components/ToastContext';
import '../css/OpnamestatenOverzicht.css';

const OpnamestatenOverzicht = () => {
  const toast = useToast();
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [stepConfigs, setStepConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, completed, in_progress, invited
  const [inviteOpen, setInviteOpen] = useState(false);

  useEffect(() => {
    fetchSessions();
    fetchStepConfigs();
  }, []);

  const fetchStepConfigs = async () => {
    try {
      const response = await api.get('/api/inspections/step-config');
      setStepConfigs(response.data);
    } catch (err) {
      console.error('Error fetching step configs:', err);
    }
  };

  const fetchSessions = async () => {
    try {
      setLoading(true);

      const response = await api.get('/api/inspections/admin/sessions');

      setSessions(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setError('Kan sessies niet laden');
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionDetails = async (sessionId) => {
    try {
      const response = await api.get(`/api/inspections/admin/sessions/${sessionId}`);

      setSelectedSession(response.data);
    } catch (err) {
      console.error('Error fetching session details:', err);
      toast.error('Kan sessie details niet laden');
    }
  };

  const closeDetailView = () => {
    setSelectedSession(null);
  };

  const handleReopenSession = async (sessionId) => {
    try {
      toast.info('Sessie wordt heropend...');
      await api.post(`/api/inspections/admin/sessions/${sessionId}/reopen`);
      toast.success('Sessie succesvol heropend voor mutaties');

      // Refresh session details and list
      await fetchSessionDetails(sessionId);
      await fetchSessions();
    } catch (error) {
      console.error('Error reopening session:', error);
      toast.error(error.response?.data?.message || 'Fout bij heropenen sessie');
    }
  };

  const handleRegenerateDocument = async (sessionId) => {
    try {
      toast.info('Document wordt opnieuw gegenereerd...');
      const { data } = await api.post(`/api/inspections/admin/sessions/${sessionId}/regenerate-document`);
      if (data?.sheetWarning) toast.warning(data.sheetWarning);
      else toast.success('Document succesvol geregenereerd');

      // Refresh session details to show new document link
      await fetchSessionDetails(sessionId);
    } catch (error) {
      console.error('Error regenerating document:', error);
      toast.error(error.response?.data?.message || 'Fout bij regenereren document');
    }
  };

  const handleGenerateDocument = async (sessionId) => {
    try {
      toast.info('Document wordt gegenereerd...');
      const { data } = await api.post(`/api/inspections/admin/sessions/${sessionId}/generate-document`);
      if (data?.sheetWarning) toast.warning(data.sheetWarning);
      else toast.success('Document succesvol gegenereerd');

      // Refresh session details to show new document link
      await fetchSessionDetails(sessionId);
    } catch (error) {
      console.error('Error generating document:', error);
      toast.error(error.response?.data?.message || 'Fout bij genereren document');
    }
  };

  const handleExtendLink = async (sessionId) => {
    try {
      toast.info('Link wordt verlengd...');
      await api.post(`/api/inspections/admin/sessions/${sessionId}/extend-link`);
      toast.success('Link verlengd met 30 dagen');
      await fetchSessionDetails(sessionId);
    } catch (error) {
      console.error('Error extending link:', error);
      toast.error('Fout bij verlengen link');
    }
  };

  const handleCopyLink = async (session, e) => {
    e.stopPropagation();
    if (!session.token) {
      toast.error('Geen link beschikbaar voor deze sessie');
      return;
    }
    try {
      const response = await api.post(
        `/api/inspections/admin/sessions/${session._id}/extend-link`,
        { days: 365 }
      );
      const url = `${window.location.origin}/opnamestaat/${session.token}`;
      await navigator.clipboard.writeText(url);
      const expiry = new Date(response.data.expiresAt).toLocaleDateString('nl-NL', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
      toast.success(`Link gekopieerd, geldig tot ${expiry}`);
      await fetchSessions();
    } catch (error) {
      console.error('Error copying link:', error);
      toast.error('Fout bij kopiëren link');
    }
  };

  const [uploading, setUploading] = useState(false);

  const handleUploadPhoto = async (sessionId, section, file) => {
    try {
      setUploading(true);
      toast.info('Foto wordt geüpload naar Google Drive...');
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('section', section);

      const response = await api.post(`/api/inspections/admin/sessions/${sessionId}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message || 'Foto succesvol toegevoegd');
      await fetchSessionDetails(sessionId);
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error(error.response?.data?.error || 'Fout bij uploaden foto');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadToDrive = async (sessionId) => {
    try {
      toast.info('Foto\'s worden naar Google Drive geüpload...');
      const response = await api.post(`/api/inspections/admin/sessions/${sessionId}/upload-to-drive`);
      toast.success(response.data.message);
      await fetchSessionDetails(sessionId);
    } catch (error) {
      console.error('Error uploading to drive:', error);
      toast.error(error.response?.data?.error || 'Fout bij uploaden naar Drive');
    }
  };

  const handleCleanupPhotos = async (sessionId) => {
    try {
      toast.info('Verloren foto\'s worden verwijderd...');
      const response = await api.post(`/api/inspections/admin/sessions/${sessionId}/cleanup-photos`);
      toast.success(response.data.message);
      await fetchSessionDetails(sessionId);
    } catch (error) {
      console.error('Error cleaning up photos:', error);
      toast.error('Fout bij opruimen foto\'s');
    }
  };

  const handleUpdateHuisnummer = async (sessionId, newHuisnummer) => {
    try {
      await api.patch(`/api/inspections/admin/sessions/${sessionId}/huisnummer`, {
        huisnummer: newHuisnummer
      });
      toast.success(`Huisnummer gewijzigd naar ${newHuisnummer}`);
      await fetchSessionDetails(sessionId);
      await fetchSessions();
    } catch (error) {
      console.error('Error updating huisnummer:', error);
      toast.error(error.response?.data?.error || 'Fout bij wijzigen huisnummer');
    }
  };

  const handleSendApprovalEmail = async (sessionId) => {
    try {
      toast.info('Goedkeuringsmail wordt verstuurd...');
      const response = await api.post(`/api/inspections/admin/sessions/${sessionId}/send-approval-email`, {
        inspectorName: 'Dick en Henry'
      });
      toast.success(response.data.message || 'Goedkeuringsmail succesvol verstuurd');

      // Refresh session details to update approvalEmailSentAt
      await fetchSessionDetails(sessionId);
    } catch (error) {
      console.error('Error sending approval email:', error);
      toast.error(error.response?.data?.message || 'Fout bij versturen goedkeuringsmail');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Niet beschikbaar';
    return new Date(dateString).toLocaleString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      invited: { label: 'Uitgenodigd', className: 'status-invited' },
      in_progress: { label: 'Bezig', className: 'status-in-progress' },
      completed: { label: 'Voltooid', className: 'status-completed' },
      reviewed: { label: 'Beoordeeld', className: 'status-reviewed' }
    };

    const statusInfo = statusMap[status] || { label: status, className: '' };

    return <span className={`status-badge ${statusInfo.className}`}>{statusInfo.label}</span>;
  };

  const filteredSessions = sessions.filter(session => {
    if (filter === 'all') return true;
    return session.status === filter;
  });

  if (loading) {
    return (
      <BeheerdersportaalSidebar>
        <main className="dashboard">
          <div className="loading-message">Sessies laden...</div>
        </main>
      </BeheerdersportaalSidebar>
    );
  }

  return (
    <BeheerdersportaalSidebar>
      <main className="dashboard">
        <div className="overzicht-container">
          <div className="overzicht-header">
            <div className="overzicht-header-titles">
              <h1>Opnamestaten</h1>
              <p className="subtitle">Beheer uitnodigingen, status en inhoud van opnamestaten</p>
            </div>
            <div className="overzicht-header-actions">
              <button
                type="button"
                className="btn-primary-invite"
                onClick={() => setInviteOpen(true)}
              >
                + Nieuwe uitnodigingen
              </button>
              <Link to="/opnamestaten-vragen" className="link-vragenbeheer">
                ⚙ Vragen beheren
              </Link>
            </div>
          </div>

          {error && (
            <div className="error-banner">
              {error}
              <button onClick={fetchSessions} className="btn-retry">
                Opnieuw proberen
              </button>
            </div>
          )}

          {/* Filters */}
          <div className="filter-bar">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              Alle ({sessions.length})
            </button>
            <button
              className={`filter-btn ${filter === 'invited' ? 'active' : ''}`}
              onClick={() => setFilter('invited')}
            >
              Uitgenodigd ({sessions.filter(s => s.status === 'invited').length})
            </button>
            <button
              className={`filter-btn ${filter === 'in_progress' ? 'active' : ''}`}
              onClick={() => setFilter('in_progress')}
            >
              Bezig ({sessions.filter(s => s.status === 'in_progress').length})
            </button>
            <button
              className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
              onClick={() => setFilter('completed')}
            >
              Voltooid ({sessions.filter(s => s.status === 'completed').length})
            </button>
          </div>

          {/* Sessions List */}
          <div className="sessions-list">
            {filteredSessions.length === 0 ? (
              <div className="no-results">Geen sessies gevonden</div>
            ) : (
              filteredSessions.map(session => (
                <div
                  key={session._id}
                  className="session-card"
                  onClick={() => fetchSessionDetails(session._id)}
                  style={{ position: 'relative' }}
                >
                  {session.token && session.status !== 'completed' && session.status !== 'reviewed' && (
                    <button
                      onClick={(e) => handleCopyLink(session, e)}
                      title="Kopieer opnamestaat-link (verlengt naar 365 dagen)"
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        padding: '6px 10px',
                        backgroundColor: '#246834',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      🔗 Kopieer link
                    </button>
                  )}
                  <div className="session-info">
                    <h3>{session.huisnummer}</h3>
                    <p className="session-bewoner">{session.bewoner?.naam}</p>
                    <p className="session-email">{session.bewoner?.email}</p>
                  </div>
                  <div className="session-meta">
                    {getStatusBadge(session.status)}
                    {session.submittedAt && (
                      <p className="session-date">
                        <small>Ingediend: {formatDate(session.submittedAt)}</small>
                      </p>
                    )}
                    {session.expiresAt && session.status !== 'completed' && session.status !== 'reviewed' && (
                      <p className="session-date">
                        <small style={{ color: new Date(session.expiresAt) < new Date() ? '#c33' : '#666' }}>
                          Verloopt: {new Date(session.expiresAt).toLocaleDateString('nl-NL', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </small>
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Invite Modal */}
        {inviteOpen && (
          <InviteModal
            onClose={() => setInviteOpen(false)}
            onSent={() => { setInviteOpen(false); fetchSessions(); }}
          />
        )}

        {/* Detail Modal */}
        {selectedSession && (
          <div className="modal-overlay" onClick={closeDetailView}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Opnamestaat Details - {selectedSession.huisnummer}</h2>
                <button className="close-button" onClick={closeDetailView}>×</button>
              </div>

              <div className="modal-body">
                <SessionDetails
                  session={selectedSession}
                  stepConfigs={stepConfigs}
                  onReopenSession={handleReopenSession}
                  onRegenerateDocument={handleRegenerateDocument}
                  onGenerateDocument={handleGenerateDocument}
                  onSendApprovalEmail={handleSendApprovalEmail}
                  onExtendLink={handleExtendLink}
                  onUploadPhoto={handleUploadPhoto}
                  onUpdateHuisnummer={handleUpdateHuisnummer}
                  onUploadToDrive={handleUploadToDrive}
                  onCleanupPhotos={handleCleanupPhotos}
                  uploading={uploading}
                />
              </div>
            </div>
          </div>
        )}
      </main>
    </BeheerdersportaalSidebar>
  );
};

// Invite modal — bewoners selecteren en uitnodiging sturen
const InviteModal = ({ onClose, onSent }) => {
  const toast = useToast();
  const [bewoners, setBewoners] = useState([]);
  const [selectedBewoners, setSelectedBewoners] = useState([]);
  const [afspraakDatum, setAfspraakDatum] = useState('');
  const [afspraakTijd, setAfspraakTijd] = useState('19:30 uur en 21:00 uur');
  const [inspectorName, setInspectorName] = useState('');
  const [inspectorEmail, setInspectorEmail] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [inspectorOnly, setInspectorOnly] = useState(false);
  const [mode, setMode] = useState('direct'); // 'direct' = bewoner zelf, 'inspecteur' = afspraak inplannen
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/api/bewoners/get-all')
      .then(res => {
        const active = res.data.filter(b => !b.isFormerResident && b.email && b.email.trim() !== '');
        setBewoners(active);
      })
      .catch(err => {
        console.error('Error fetching bewoners:', err);
        toast.error('Fout bij ophalen bewoners');
      });
  }, [toast]);

  const filteredBewoners = !searchTerm ? bewoners : bewoners.filter(b => {
    const s = searchTerm.toLowerCase();
    return (b.naam && b.naam.toLowerCase().includes(s)) ||
           (b.nummer && b.nummer.toString().includes(s)) ||
           (b.email && b.email.toLowerCase().includes(s));
  });

  const toggleBewoner = (email) => {
    setSelectedBewoners(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  const toggleAll = () => {
    if (selectedBewoners.length === filteredBewoners.length) {
      setSelectedBewoners([]);
    } else {
      setSelectedBewoners(filteredBewoners.map(b => b.email));
    }
  };

  const handleSend = async () => {
    if (selectedBewoners.length === 0) {
      toast.error('Selecteer minimaal één bewoner');
      return;
    }
    if (mode === 'inspecteur' && !afspraakDatum) {
      toast.error('Vul een afspraakdatum in');
      return;
    }
    if (mode === 'inspecteur' && inspectorOnly && !inspectorEmail) {
      toast.error('Vul het email van de inspecteur in');
      return;
    }

    setLoading(true);
    try {
      const endpoint = (mode === 'inspecteur' && inspectorOnly)
        ? '/api/inspections/admin/send-inspector-link'
        : '/api/inspections/admin/send-invitations';

      const res = await api.post(endpoint, {
        emails: selectedBewoners,
        afspraakDatum: mode === 'inspecteur' ? afspraakDatum : '',
        afspraakTijd: mode === 'inspecteur' ? afspraakTijd : '',
        inspectorName: mode === 'inspecteur' ? inspectorName : '',
        inspectorEmail: mode === 'inspecteur' ? inspectorEmail : ''
      });

      const result = res.data;
      let msg = `${result.sent} uitnodigingen verzonden`;
      if (result.failed > 0) {
        msg += ` (${result.failed} mislukt)`;
        if (result.failedDetails && result.failedDetails.length > 0) {
          const reasons = result.failedDetails.map(f => `${f.email}: ${f.reason}`).join(', ');
          msg += ` - ${reasons}`;
        }
      }
      if (result.sent > 0) {
        toast.success(msg);
        onSent();
      } else {
        toast.error(msg);
      }
    } catch (err) {
      console.error('Error sending invitations:', err);
      toast.error('Fout bij verzenden uitnodigingen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content invite-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Nieuwe uitnodigingen versturen</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <section className="invite-section">
            <h3>Wie vult de onderhoudscheck in?</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <label style={{
                display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer',
                padding: '12px', borderRadius: '6px',
                border: `2px solid ${mode === 'direct' ? '#3498db' : '#e0e0e0'}`,
                background: mode === 'direct' ? '#f4faff' : 'transparent'
              }}>
                <input
                  type="radio"
                  name="invite-mode"
                  checked={mode === 'direct'}
                  onChange={() => setMode('direct')}
                  style={{ marginTop: '4px' }}
                />
                <div>
                  <div style={{ fontWeight: 600 }}>Bewoner vult zelf in</div>
                  <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>
                    Bewoner ontvangt een link en doet de check op eigen tempo. Geen afspraak nodig.
                  </div>
                </div>
              </label>
              <label style={{
                display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer',
                padding: '12px', borderRadius: '6px',
                border: `2px solid ${mode === 'inspecteur' ? '#3498db' : '#e0e0e0'}`,
                background: mode === 'inspecteur' ? '#f4faff' : 'transparent'
              }}>
                <input
                  type="radio"
                  name="invite-mode"
                  checked={mode === 'inspecteur'}
                  onChange={() => setMode('inspecteur')}
                  style={{ marginTop: '4px' }}
                />
                <div>
                  <div style={{ fontWeight: 600 }}>Inspecteur komt langs</div>
                  <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>
                    Bewoner krijgt een afspraakuitnodiging en zorgt dat hij/zij thuis is.
                  </div>
                </div>
              </label>
            </div>
          </section>

          {mode === 'inspecteur' && (
            <section className="invite-section">
              <h3>Afspraak details</h3>
              <div className="invite-form-grid">
                <div className="invite-form-group">
                  <label>Afspraakdatum</label>
                  <input
                    type="text"
                    value={afspraakDatum}
                    onChange={(e) => setAfspraakDatum(e.target.value)}
                    placeholder="bijv. 27 november"
                  />
                  <small>Bijvoorbeeld: 27 november</small>
                </div>
                <div className="invite-form-group">
                  <label>Tijdvak</label>
                  <input
                    type="text"
                    value={afspraakTijd}
                    onChange={(e) => setAfspraakTijd(e.target.value)}
                    placeholder="bijv. 19:30 uur en 21:00 uur"
                  />
                </div>
                <div className="invite-form-group">
                  <label>Naam inspecteur</label>
                  <input
                    type="text"
                    value={inspectorName}
                    onChange={(e) => setInspectorName(e.target.value)}
                    placeholder="bijv. Dick en Henry"
                  />
                </div>
                <div className="invite-form-group">
                  <label>Email inspecteur</label>
                  <input
                    type="email"
                    value={inspectorEmail}
                    onChange={(e) => setInspectorEmail(e.target.value)}
                    placeholder="bijv. inspecteur@vvepolderzicht.nl"
                  />
                </div>
              </div>
            </section>
          )}

          <section className="invite-section">
            <div className="invite-section-header">
              <h3>Selecteer bewoners ({selectedBewoners.length} geselecteerd)</h3>
              <div className="invite-section-actions">
                <input
                  type="text"
                  placeholder="Zoek op naam, nummer of email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="invite-search"
                />
                <button onClick={toggleAll} className="btn-invite-secondary">
                  {selectedBewoners.length === filteredBewoners.length && filteredBewoners.length > 0
                    ? 'Deselecteer alles'
                    : 'Selecteer alles'}
                </button>
              </div>
            </div>

            <div className="invite-bewoners-list">
              {filteredBewoners.length === 0 ? (
                <p className="no-results">Geen bewoners gevonden</p>
              ) : (
                filteredBewoners.map(b => (
                  <label key={b._id} className="invite-bewoner-row">
                    <input
                      type="checkbox"
                      checked={selectedBewoners.includes(b.email)}
                      onChange={() => toggleBewoner(b.email)}
                    />
                    <div className="invite-bewoner-info">
                      <span className="invite-bewoner-naam">{b.naam || 'Onbekend'}</span>
                      <span className="invite-bewoner-meta">
                        Huisnummer: {b.nummer || 'Onbekend'} | {b.email}
                      </span>
                    </div>
                  </label>
                ))
              )}
            </div>
          </section>

          <section className="invite-section invite-footer">
            {mode === 'inspecteur' && (
              <label className="invite-inspector-only">
                <input
                  type="checkbox"
                  checked={inspectorOnly}
                  onChange={(e) => setInspectorOnly(e.target.checked)}
                />
                <span>Alleen naar inspecteur sturen (niet naar bewoner)</span>
              </label>
            )}
            <div className="invite-footer-actions">
              <button onClick={onClose} className="btn-invite-secondary" disabled={loading}>
                Annuleren
              </button>
              <button
                onClick={handleSend}
                disabled={loading || selectedBewoners.length === 0}
                className="btn-invite-primary"
              >
                {loading
                  ? 'Verzenden...'
                  : (mode === 'inspecteur' && inspectorOnly)
                    ? `Stuur link naar inspecteur (${selectedBewoners.length} adressen)`
                    : `Verstuur ${selectedBewoners.length} uitnodigingen`}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

// Component to display session details
const SessionDetails = ({ session, stepConfigs, onReopenSession, onRegenerateDocument, onGenerateDocument, onSendApprovalEmail, onExtendLink, onUploadPhoto, onUpdateHuisnummer, onUploadToDrive, onCleanupPhotos, uploading }) => {
  const [editingHuisnummer, setEditingHuisnummer] = React.useState(false);
  const [newHuisnummer, setNewHuisnummer] = React.useState(session.huisnummer);
  const [uploadSection, setUploadSection] = React.useState('');
  const fileInputRef = React.useRef(null);
  const sections = [
    { key: 'toegang', title: 'Toegang & Sleutels' },
    { key: 'voordeur', title: 'Voordeur' },
    { key: 'kantelkiepramen', title: 'Kantel/Kiepramen' },
    { key: 'balkonKlein', title: 'Balkon Klein' },
    { key: 'balkonGroot', title: 'Balkon Groot' },
    { key: 'gebruikersonderhoud', title: 'Gebruikersonderhoud' },
    { key: 'algemeen', title: 'Algemeen' }
  ];

  // Helper function to check if a field is visible based on step config
  const isFieldVisible = (stepKey, fieldKey) => {
    const stepConfig = stepConfigs.find(config => config.stepId === stepKey);
    if (!stepConfig || !stepConfig.fields) return true; // Show by default if no config

    const field = stepConfig.fields.find(f => {
      const fieldPath = f.fieldPath || '';
      const lastPart = fieldPath.split('.').pop();
      return lastPart === fieldKey;
    });

    return field ? field.visible !== false : true; // Show by default if field not found
  };

  // Field labels for better display
  const fieldLabels = {
    // Toegang
    gebouw: 'Aantal sleutels gebouw',
    tussendeurTag: 'Aantal toegangstags tussendeur',
    appartement: 'Aantal sleutels appartement',
    berging: 'Aantal sleutels berging',
    // Common fields
    houtaantasting: 'Houtaantasting',
    openVerbindingen: 'Open verbindingen',
    scheuren: 'Scheuren',
    klemmenDraaiendeDelen: 'Klemmen draaiende delen',
    scherpeKanten: 'Scherpe kanten',
    losRakenVerf: 'Lossend verfwerk',
    losRakenBeitslaklaag: 'Lossende beits-/laklaag',
    aftandingsGlasvlakken: 'Aftandingen glasvlakken',
    scheurenGlasvlakken: 'Scheuren glasvlakken',
    broosGlasvlakken: 'Broos glas',
    kitverbinding: 'Kitverbinding',
    loopvlak: 'Loopvlak',
    buisframes: 'Buisframes',
    matten: 'Matten',
    staat: 'Staat',
    opmerkingen: 'Opmerkingen',
    // Kantelkiepramen/Ramen
    tochtafdichting: 'Tochtafdichting',
    scharnieren: 'Scharnieren',
    sluitwerk: 'Sluitwerk',
    // Gebruikersonderhoud - Apparaten
    boiler: 'Boiler',
    boilerOpmerking: 'Opmerking boiler',
    gasaansluiting: 'Gasaansluiting',
    gasaansluitingOpmerking: 'Opmerking gasaansluiting',
    kookplaat: 'Kookplaat',
    oven: 'Oven(s)',
    koelkast: 'Koelkast',
    diepvries: 'Diepvries',
    cooker: 'Cooker (afzuigkap)',
    mechanischeAfvoer: 'Mechanische afvoer',
    apparatuurOpmerkingen: 'Opmerkingen apparatuur',
    // Gebruikersonderhoud - Aansluitingen
    aansluitingTV: 'Aansluiting TV',
    tvOpmerking: 'Opmerking TV',
    sdl: 'ADSL',
    glasvezel: 'Glasvezel',
    internetOpmerking: 'Opmerking internet',
    analogeTelefoonlijn: 'Analoge telefoonlijn',
    telefoonOpmerking: 'Opmerking telefoon',
    intercomGeluid: 'Intercom Geluid',
    intercomBeeld: 'Intercom Beeld',
    intercomOpmerking: 'Opmerking intercom',
    // Gebruikersonderhoud - Ventilatie & Verwarming
    luchtafvoerKeuken: 'Luchtafvoer keuken',
    luchtafvoerBadkamer: 'Luchtafvoer badkamer',
    luchtafvoerWC: 'Luchtafvoer WC',
    afvoerOpmerkingen: 'Opmerkingen afvoer',
    extraGroepen: 'Extra groepen meterkast',
    groepenOpmerking: 'Opmerking groepen',
    verwarmingWoonkamer1: 'Verwarming Woonkamer 1',
    verwarmingWoonkamer2: 'Verwarming Woonkamer 2',
    verwarmingKeuken: 'Verwarming Keuken',
    verwarmingBorrelkamer: 'Verwarming Borrelkamer',
    verwarmingWerkkamer: 'Verwarming Werkkamer',
    verwarmingSlaapkamer: 'Verwarming Slaapkamer',
    verwarmingBadkamer: 'Verwarming Badkamer',
    verwarmingOpmerking: 'Opmerking verwarming',
    // Gebruikersonderhoud - Afvoer & Fotos
    afvoerBadkamer: 'Afvoer badkamer',
    afvoerKeuken: 'Afvoer keuken',
    afvoerWC: 'Afvoer WC',
    afvoerOpmerking: 'Opmerking afvoer',
    foto1: 'Foto 1',
    foto1Opmerking: 'Opmerking foto 1',
    foto2: 'Foto 2',
    foto2Opmerking: 'Opmerking foto 2',
    foto3: 'Foto 3',
    foto3Opmerking: 'Opmerking foto 3',
    foto4: 'Foto 4',
    foto4Opmerking: 'Opmerking foto 4',
    // Gebruikersonderhoud - Aanpassingen
    aanpassing1: 'Aanpassing 1',
    aanpassing1Opmerking: 'Opmerking aanpassing 1',
    aanpassing2: 'Aanpassing 2',
    aanpassing2Opmerking: 'Opmerking aanpassing 2',
    wens: 'Wens',
    wensOpmerking: 'Opmerking wens',
    opmerking1: 'Opmerking 1',
    toelichting1: 'Toelichting 1',
    opmerking2: 'Opmerking 2',
    toelichting2: 'Toelichting 2',
    afspraak1: 'Afspraak 1',
    actiehouder1: 'Actiehouder 1',
    afspraak2: 'Afspraak 2',
    actiehouder2: 'Actiehouder 2',
    afspraak3: 'Afspraak 3',
    actiehouder3: 'Actiehouder 3',
    afspraak4: 'Afspraak 4',
    actiehouder4: 'Actiehouder 4',
    afspraak5: 'Afspraak 5',
    actiehouder5: 'Actiehouder 5',
    // Algemeen
    aanpassingenAlgemeneGedeelte: 'Aanpassingen aan algemene gedeeltes',
    aantalGroepenMeterkast: 'Aantal groepen meterkast',
    aansluitingenTVInternet: 'Aansluitingen TV & Internet',
    algemeenStateInterieur: 'Algemene staat interieur',
    toekomstigeOnderhoudsplannen: 'Toekomstige onderhoudsplannen',
    eventueleGebreken: 'Eventuele gebreken',
    extraOpmerkingen: 'Extra opmerkingen',
    extraDocumenten: 'Extra documenten',
    // Old gebruikersonderhoud fields (keep for backwards compatibility)
    witraamwerk: 'Witraamwerk',
    kozijnen: 'Kozijnen',
    glas: 'Glas',
    galerij: 'Galerij',
    balkon: 'Balkon',
    deurkleurenBinnendeur: 'Deuren binnen',
    vloerbedekkingBinnen: 'Vloerbedekking',
    wandafwerking: 'Wandafwerking',
    plafondafwerking: 'Plafondafwerking'
  };

  const renderStarRating = (value) => {
    if (value === null || value === undefined) return '-';
    const stars = '★'.repeat(value) + '☆'.repeat(5 - value);
    return `${stars} (${value}/5)`;
  };

  // Extract file ID from Google Drive URL
  const extractDriveFileId = (url) => {
    if (!url) return null;
    // Format: https://drive.google.com/file/d/FILE_ID/view?usp=drivesdk
    const match = url.match(/\/file\/d\/([^/]+)/);
    return match ? match[1] : null;
  };

  const renderPhotos = (fotos) => {
    if (!fotos || fotos.length === 0) return null;

    return (
      <div className="photos-section">
        <h5>Foto's</h5>
        <div className="photos-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {fotos.map((foto, idx) => {
            const linkUrl = foto.driveUrl || foto.url;
            const fileId = extractDriveFileId(linkUrl);
            // Serve via our backend proxy — drive.google.com/thumbnail hotlinks are
            // unreliable for service-account / shared-drive files and often fail to render.
            const thumbnailUrl = fileId
              ? `${api.defaults.baseURL}/api/inspections/photo/${fileId}`
              : null;

            return (
              <div key={idx} className="photo-item">
                {linkUrl ? (
                  <a
                    href={linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      textDecoration: 'none'
                    }}
                  >
                    {thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt={foto.filename}
                        style={{
                          width: '150px',
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '2px solid #ddd',
                          transition: 'border-color 0.2s'
                        }}
                        onError={(e) => {
                          // If thumbnail fails, show placeholder
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div style={{
                      display: thumbnailUrl ? 'none' : 'flex',
                      width: '150px',
                      height: '150px',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '8px',
                      border: '2px solid #ddd',
                      flexDirection: 'column',
                      gap: '5px'
                    }}>
                      <span style={{ fontSize: '32px' }}>📷</span>
                      <span style={{ fontSize: '10px', color: '#666', textAlign: 'center', padding: '0 5px' }}>
                        {foto.filename}
                      </span>
                    </div>
                  </a>
                ) : (
                  <div style={{
                    width: '150px',
                    height: '150px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f0f0f0',
                    borderRadius: '8px',
                    border: '2px solid #ddd',
                    flexDirection: 'column',
                    gap: '5px'
                  }}>
                    <span style={{ fontSize: '32px' }}>📷</span>
                    <span style={{ fontSize: '10px', color: '#666', textAlign: 'center', padding: '0 5px' }}>
                      {foto.filename}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderValue = (key, value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'boolean') return value ? 'Ja' : 'Nee';

    // Arrays and objects cannot be rendered as React children
    if (Array.isArray(value)) return `${value.length} item(s)`;
    if (typeof value === 'object') return JSON.stringify(value);

    // Number fields that should NOT be star ratings (access keys, etc)
    const nonStarRatingFields = ['gebouw', 'tussendeurTag', 'appartement', 'berging'];

    // Check if this is a star rating (number between 1-5)
    // Most numeric inspection fields are star ratings except access keys
    if (typeof value === 'number' && value >= 1 && value <= 5 && !nonStarRatingFields.includes(key)) {
      return renderStarRating(value);
    }

    if (typeof value === 'number') return value;
    return value;
  };

  const renderField = (key, value, sectionKey, indent = false) => {
    // Skip certain keys
    if (key === 'fotos' || key === 'extraDocumenten' || key === '_id' || key === 'slaapkamer' ||
        key === 'woonkamerEnkel' || key === 'woonkamerDubbel') return null;

    // Check field visibility based on step configuration
    if (!isFieldVisible(sectionKey, key)) return null;

    const label = fieldLabels[key] || key.charAt(0).toUpperCase() + key.slice(1);

    return (
      <tr key={key} className={indent ? 'indent' : ''}>
        <td><strong>{label}:</strong></td>
        <td>{renderValue(key, value)}</td>
      </tr>
    );
  };

  const renderNestedObject = (obj, sectionKey) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;

    const entries = Object.entries(obj).filter(([key, value]) => {
      // Skip special keys, nested objects that should be rendered separately, and arrays of objects (like fotos, extraDocumenten)
      if (key === 'fotos' || key === '_id' || key === 'slaapkamer' || key === 'woonkamerEnkel' || key === 'woonkamerDubbel') return false;
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') return false;
      return true;
    });

    if (entries.length === 0) return null;

    return entries.map(([key, value]) => {
      if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
        // Nested object - render as a subsection
        const label = fieldLabels[key] || key.charAt(0).toUpperCase() + key.slice(1);
        return (
          <React.Fragment key={key}>
            <tr className="subsection-header">
              <td colSpan="2"><strong>{label}</strong></td>
            </tr>
            {Object.entries(value).map(([nestedKey, nestedValue]) =>
              renderField(nestedKey, nestedValue, sectionKey, true)
            )}
          </React.Fragment>
        );
      }
      return renderField(key, value, sectionKey);
    });
  };

  return (
    <div className="session-details">
      <div className="detail-section">
        <h3>Algemene Informatie</h3>
        <table className="detail-table">
          <tbody>
            <tr>
              <td><strong>Huisnummer:</strong></td>
              <td>
                {editingHuisnummer ? (
                  <span style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="number"
                      min="211"
                      max="305"
                      value={newHuisnummer}
                      onChange={(e) => setNewHuisnummer(e.target.value)}
                      style={{ width: '80px', padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <button
                      onClick={() => { onUpdateHuisnummer(session._id, newHuisnummer); setEditingHuisnummer(false); }}
                      style={{ padding: '4px 10px', backgroundColor: '#246834', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                    >Opslaan</button>
                    <button
                      onClick={() => { setEditingHuisnummer(false); setNewHuisnummer(session.huisnummer); }}
                      style={{ padding: '4px 10px', backgroundColor: '#999', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                    >Annuleer</button>
                  </span>
                ) : (
                  <span>
                    {session.huisnummer}
                    <button
                      onClick={() => setEditingHuisnummer(true)}
                      style={{ marginLeft: '10px', padding: '2px 8px', backgroundColor: '#f0f0f0', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}
                    >Wijzig</button>
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td><strong>Bewoner:</strong></td>
              <td>{session.bewoner?.naam}</td>
            </tr>
            <tr>
              <td><strong>Email:</strong></td>
              <td>{session.bewoner?.email}</td>
            </tr>
            <tr>
              <td><strong>Verdieping:</strong></td>
              <td>{renderValue('verdieping', session.verdieping)}</td>
            </tr>
            <tr>
              <td><strong>Gebouwzijde:</strong></td>
              <td>{renderValue('gebouwzijde', session.gebouwzijde)}</td>
            </tr>
            <tr>
              <td><strong>Status:</strong></td>
              <td>{session.status}</td>
            </tr>
            <tr>
              <td><strong>Ingediend op:</strong></td>
              <td>{session.submittedAt ? new Date(session.submittedAt).toLocaleString('nl-NL') : '-'}</td>
            </tr>
            {session.generatedDocument?.documentUrl && (
              <tr>
                <td><strong>Google Doc:</strong></td>
                <td>
                  <a
                    href={session.generatedDocument.documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#246834', textDecoration: 'underline' }}
                  >
                    📄 Bekijk Opnamestaat Document
                  </a>
                  <br />
                  <small style={{ color: '#666' }}>
                    Gegenereerd: {new Date(session.generatedDocument.generatedAt).toLocaleString('nl-NL')}
                  </small>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Action buttons */}
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {(session.status === 'invited' || session.status === 'in_progress') && (
            <button
              onClick={() => onExtendLink(session._id)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#FF9800',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              🔗 Verleng Link (30 dagen)
            </button>
          )}

          {session.status === 'completed' && (
            <button
              onClick={() => onReopenSession(session._id)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              🔓 Heropenen voor Mutaties
            </button>
          )}

          {session.generatedDocument?.documentUrl ? (
            <button
              onClick={() => onRegenerateDocument(session._id)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#246834',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              🔄 Document Regenereren
            </button>
          ) : (
            session.status === 'completed' && (
              <button
                onClick={() => onGenerateDocument(session._id)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#246834',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                📝 Document Genereren
              </button>
            )
          )}

          {session.generatedDocument?.documentUrl && (
            <button
              onClick={() => onSendApprovalEmail(session._id)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              title={session.approvalEmailSentAt ? `Laatst verstuurd: ${new Date(session.approvalEmailSentAt).toLocaleString('nl-NL')}` : 'Nog niet verstuurd'}
            >
              {session.approvalEmailSentAt ? '📧 Goedkeuringsmail Opnieuw Versturen' : '📧 Verstuur Goedkeuringsmail'}
            </button>
          )}

          <button
            onClick={() => onUploadToDrive(session._id)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#4285F4',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            ☁️ Upload foto's naar Drive
          </button>

          <button
            onClick={() => { if (window.confirm('Alle foto\'s zonder Google Drive link verwijderen?')) onCleanupPhotos(session._id); }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            🗑️ Verloren foto's opruimen
          </button>
        </div>
      </div>

      {/* Hidden file input for photo uploads */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/jpeg,image/png,application/pdf"
        onChange={(e) => {
          if (e.target.files[0] && uploadSection) {
            onUploadPhoto(session._id, uploadSection, e.target.files[0]);
          }
          e.target.value = '';
        }}
      />

      {sections.map(({ key, title }) => {
        const sectionData = session[key];
        if (!sectionData) return null;

        return (
          <div key={key} className="detail-section">
            <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {title}
              {key !== 'toegang' && (
                <button
                  onClick={() => { setUploadSection(key); fileInputRef.current?.click(); }}
                  disabled={uploading}
                  style={{
                    padding: '4px 12px',
                    backgroundColor: uploading ? '#999' : '#246834',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: uploading ? 'wait' : 'pointer',
                    fontSize: '12px',
                    fontWeight: 'normal'
                  }}
                >
                  {uploading ? 'Bezig met uploaden...' : '+ Foto toevoegen'}
                </button>
              )}
            </h3>

            {/* Render photos first if they exist */}
            {sectionData.fotos && renderPhotos(sectionData.fotos)}

            {/* Render extra documenten (also photos) */}
            {sectionData.extraDocumenten && sectionData.extraDocumenten.length > 0 && renderPhotos(sectionData.extraDocumenten)}

            {/* Render all other fields */}
            <table className="detail-table">
              <tbody>
                {renderNestedObject(sectionData, key)}
              </tbody>
            </table>

            {/* Special handling for kantelkiepramen subsections */}
            {key === 'kantelkiepramen' && (
              <>
                {['slaapkamer', 'woonkamerEnkel', 'woonkamerDubbel'].map(room => {
                  const roomData = sectionData[room];

                  // Check if roomData has any meaningful data
                  const hasData = roomData && (
                    roomData.tochtafdichting !== undefined ||
                    roomData.scharnieren !== undefined ||
                    roomData.sluitwerk !== undefined ||
                    (roomData.fotos && roomData.fotos.length > 0) ||
                    roomData.opmerkingen
                  );

                  if (!hasData) {
                    return null;
                  }

                  const roomLabels = {
                    slaapkamer: 'Slaapkamer',
                    woonkamerEnkel: 'Eetkamer',
                    woonkamerDubbel: 'Woonkamer'
                  };

                  return (
                    <div key={room} className="subsection" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                      <h4 style={{ color: '#246834', marginBottom: '10px' }}>{roomLabels[room]}</h4>
                      {roomData.fotos && renderPhotos(roomData.fotos)}
                      <table className="detail-table">
                        <tbody>
                          {renderNestedObject(roomData, 'ramen')}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default OpnamestatenOverzicht;
